import React, { useEffect } from 'react';
import { css } from '@emotion/css';
import { useLocation } from 'react-router-dom';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Footer } from '../../shared/components/footer/footer';
import { WhatsAppButton } from '../../shared/components/whatsapp-button/whatsapp-button';
import { EventsHeroBanner } from '../../features/events/components/events-hero-banner/events-hero-banner';
import { SectionCard } from '../../shared/ui/section-card/section-card';
import { SectionTitle } from '../../shared/ui/section-title/section-title';
import { EventCarousel } from '../../features/events/components/event-carousel/event-carousel';
import { NewsletterSubscribe } from '../../features/notifications/components/newsletter-subscribe/newsletter-subscribe';
import { useGetEvents } from '../../features/events/hooks/events-api.hooks';
import { useLoading } from '../../features/loading';

const container = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const whatsappButtonWrapper = css({
  position: 'fixed',
  bottom: '30px',
  right: '30px',
  zIndex: 1000,
  '@media (max-width: 992px)': {
    bottom: '90px',
  },
  '@media (max-width: 768px)': {
    bottom: '90px',
    right: '20px',
  },
  '@media (max-width: 480px)': {
    bottom: '80px',
    right: '15px',
  },
});

const heroBanner = css({
  '@media (max-width: 768px)': {
    '& > div > div': {
      height: '60vh',
      minHeight: '500px',
    },
  },
  '@media (max-width: 480px)': {
    '& > div > div': {
      height: '70vh',
      minHeight: '550px',
    },
  },
});

const sectionContent = css({
  padding: '0 var(--spacing-md)',
  '@media (max-width: 768px)': {
    padding: 0,
    '& > h2': {
      marginLeft: 'var(--spacing-md)',
      marginRight: 'var(--spacing-md)',
    },
    '& > div > div': {
      paddingLeft: 'var(--spacing-md)',
    },
  },
});

export const Home: React.FC = () => {
  // Fetch Once Upon A Time events for "Próximos Eventos" section
  const { data: allEvents, isLoading: isLoadingAllEvents } = useGetEvents({
    status: 'published',
    tags: 'once-upon-a-time',
    limit: 10,
  });

  // Fetch events with "once-upon-a-time" tag for "Once Upon A Time: Classical" section
  const { data: onceUponATimeEvents, isLoading: isLoadingOnceEvents } = useGetEvents({
    status: 'published',
    tags: 'once-upon-a-time',
    limit: 10,
  });

  // Fetch other events (excluding once-upon-a-time tag)
  const { data: otherEvents, isLoading: isLoadingOtherEvents } = useGetEvents({
    status: 'published',
    excludeTags: 'once-upon-a-time',
    limit: 10,
  });

  const { setLoading, isLoading } = useLoading();
  const location = useLocation();

  useEffect(() => {
    const anyLoading = isLoadingAllEvents || isLoadingOnceEvents || isLoadingOtherEvents;
    setLoading(anyLoading);
  }, [isLoadingAllEvents, isLoadingOnceEvents, isLoadingOtherEvents, setLoading]);

  // Smooth scroll to eventos section when hash is present
  useEffect(() => {
    if (!isLoading && location.hash === '#eventos') {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        const eventosSection = document.getElementById('eventos');
        if (eventosSection) {
          // Calculate position to center the element
          const elementRect = eventosSection.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
          
          window.scrollTo({
            top: middle,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [isLoading, location.hash]);

  // Don't render content while loading
  if (isLoading) {
    return null;
  }

  return (
    <>
      <Navbar />
      <div className={container}>
        <EventsHeroBanner className={heroBanner} />
        <SectionCard id="eventos" style={{ marginTop: '7rem', marginBottom: 'var(--spacing-3xl)' }}>
          <div className={sectionContent}>
            <SectionTitle>Próximos Eventos</SectionTitle>
            {allEvents?.events && allEvents.events.length > 0 ? (
              <div style={{ marginTop: 'var(--spacing-lg)' }}>
                <EventCarousel events={allEvents.events} />
              </div>
            ) : !isLoading ? (
              <div style={{ marginTop: 'var(--spacing-lg)' }}>
                <p>Nenhum evento disponível no momento.</p>
              </div>
            ) : null}
          </div>
        </SectionCard>
        <SectionCard style={{ marginBottom: 'var(--spacing-3xl)' }}>
          <div className={sectionContent}>
            <SectionTitle>Eventos "Once Upon A Time: Classical"</SectionTitle>
            {onceUponATimeEvents?.events && onceUponATimeEvents.events.length > 0 ? (
              <div style={{ marginTop: 'var(--spacing-lg)' }}>
                <EventCarousel events={onceUponATimeEvents.events} />
              </div>
            ) : !isLoading ? (
              <div style={{ marginTop: 'var(--spacing-lg)' }}>
                <p>Nenhum evento disponível no momento.</p>
              </div>
            ) : null}
          </div>
        </SectionCard>
        <SectionCard>
          <div className={sectionContent}>
            <SectionTitle>Outros Eventos</SectionTitle>
            {otherEvents?.events && otherEvents.events.length > 0 ? (
              <div style={{ marginTop: 'var(--spacing-lg)' }}>
                <EventCarousel events={otherEvents.events} />
              </div>
            ) : !isLoading ? (
              <div style={{ marginTop: 'var(--spacing-lg)' }}>
                <p>Nenhum evento disponível no momento.</p>
              </div>
            ) : null}
          </div>
        </SectionCard>
        <NewsletterSubscribe style={{ marginTop: '7rem' }} />
      </div>
      <Footer />
      <div className={whatsappButtonWrapper}>
        <WhatsAppButton />
      </div>
    </>
  );
};

export default Home;